/**
 * `{{…}}` placeholders in a PO template's text blocks (z8nrz7f7dz).
 *
 * Resolved from a fixed ALLOW-LIST only. A template is tenant-authored text
 * that every printed order carries, so it must never be able to reach
 * anything the list does not name: no expression evaluation, no property
 * walking. An unknown placeholder is printed literally, which is also how a
 * typo shows up in the preview.
 */

import type { PoTemplateLayout, PurchaseOrderDetail } from '@/types/purchaseOrders'
import { formatPoDateValue } from './format'

/** Every placeholder a template may use, with what it stands for — the editor's cheat-sheet. */
export const PO_PLACEHOLDERS: { key: string; description: string }[] = [
  { key: 'company.legalName', description: 'Your company’s legal name' },
  { key: 'company.address', description: 'Your company’s address, on one line' },
  { key: 'company.address.street', description: 'Street' },
  { key: 'company.address.postcode', description: 'Postcode' },
  { key: 'company.address.city', description: 'City' },
  { key: 'company.address.country', description: 'Country' },
  { key: 'company.phone', description: 'Your company’s phone' },
  { key: 'company.email', description: 'Your company’s email' },
  { key: 'company.taxId', description: 'Your company’s VAT / tax ID' },
  { key: 'company.registration', description: 'Your company’s registration number' },
  { key: 'po.number', description: 'The PO number' },
  { key: 'po.date', description: 'The order date' },
  { key: 'po.revision', description: 'The revision number' },
  { key: 'po.requestedDeliveryDate', description: 'The requested delivery date' },
  { key: 'po.paymentTerms', description: 'Payment terms' },
  { key: 'po.incoterms', description: 'Incoterms' },
  { key: 'po.currency', description: 'Currency' },
  { key: 'po.buyer', description: 'Buyer' },
  { key: 'po.reference', description: 'Your reference' },
  { key: 'supplier.name', description: 'Supplier name' },
  { key: 'supplier.contact', description: 'Supplier contact' },
  { key: 'supplier.email', description: 'Supplier email' },
  { key: 'supplier.phone', description: 'Supplier phone' },
]

export type PlaceholderValues = Record<string, string>

const join = (parts: (string | null | undefined)[], sep: string) => parts.filter((p) => p && p.trim()).join(sep)

/** The allow-listed values for one order under one template. Missing values are empty strings. */
export function placeholderValues(layout: PoTemplateLayout, po: PurchaseOrderDetail): PlaceholderValues {
  const c = layout.company
  const a = c.address ?? {}
  const locale = layout.document.locale
  const values: Record<string, string | null | undefined> = {
    'company.legalName': c.legalName,
    'company.address': join([a.street, join([a.postcode, a.city], ' '), a.country], ', '),
    'company.address.street': a.street,
    'company.address.postcode': a.postcode,
    'company.address.city': a.city,
    'company.address.country': a.country,
    'company.phone': c.phone,
    'company.email': c.email,
    'company.taxId': c.taxId,
    'company.registration': c.registration,
    'po.number': po.number,
    'po.date': formatPoDateValue(po.sentAt ?? po.createdAt, locale),
    'po.revision': String(po.revision),
    'po.requestedDeliveryDate': po.requestedDeliveryDate ? formatPoDateValue(po.requestedDeliveryDate, locale) : '',
    'po.paymentTerms': po.paymentTerms,
    'po.incoterms': po.incoterms,
    'po.currency': po.currency,
    'po.buyer': po.buyer,
    'po.reference': po.reference,
    'supplier.name': po.supplierName,
    'supplier.contact': po.supplierContact,
    'supplier.email': po.supplierEmail,
    'supplier.phone': po.supplierPhone,
  }
  return Object.fromEntries(PO_PLACEHOLDERS.map(({ key }) => [key, values[key] ?? '']))
}

const PATTERN = /\{\{\s*([A-Za-z][A-Za-z0-9]*(?:\.[A-Za-z][A-Za-z0-9]*)*)\s*\}\}/g

/** Replace the allow-listed placeholders in `text`; leave anything else exactly as written. */
export function resolvePlaceholders(text: string, values: PlaceholderValues): string {
  return text.replace(PATTERN, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match,
  )
}
